import { useEffect, useMemo, useState } from "react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Copy, Eye, EyeOff, KeyRound, RefreshCw, Save } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";

function serverUrl() {
  // API_BASE may be relative or empty when served behind the same origin
  if (/^https?:\/\//.test(API_BASE)) return API_BASE.replace(/\/api$/, "");
  return window.location.origin;
}

function unitySample(baseUrl, key) {
  return `using System;
using System.Collections;
using System.Text;
using UnityEngine;
using UnityEngine.Networking;

// Attach to any GameObject. Uses the Device Key from the admin console.
public class NeuroConsoleClient : MonoBehaviour
{
    const string BaseUrl = "${baseUrl}";
    const string DeviceKey = "${key || "<DEVICE_KEY>"}";

    // Quest headsets: use a stable id. SystemInfo.deviceUniqueIdentifier works too.
    string Serial => SystemInfo.deviceUniqueIdentifier;

    // ---- Response models for /api/device/config (JsonUtility-friendly) ----
    [Serializable] public class Headset { public string id, serial, name, model, notes, status, clinician_id, patient_id; }
    [Serializable] public class Patient { public string id, name, notes, program_start_date; public int target_minutes_per_day, program_weeks; }
    [Serializable] public class Simulation { public string name, category, version, apk_url, image_url; }
    [Serializable] public class Assignment { public string id, simulation_id; public Simulation simulation; }
    [Serializable] public class DeviceConfig { public Headset headset; public Patient patient; public Assignment[] assignments; }

    void Start()
    {
        StartCoroutine(GetConfig(cfg => Debug.Log($"Patient: {cfg.patient?.name}, modules: {cfg.assignments.Length}")));
        StartCoroutine(SendEvent("session_start", 0, 0f));
    }

    // GET /api/device/config?serial=...
    // 200 = ok, 401 = bad key, 404 = headset not registered yet (send one telemetry event first).
    public IEnumerator GetConfig(Action<DeviceConfig> onDone)
    {
        var url = $"{BaseUrl}/api/device/config?serial={UnityWebRequest.EscapeURL(Serial)}";
        using var req = UnityWebRequest.Get(url);
        req.SetRequestHeader("X-Device-Key", DeviceKey);
        yield return req.SendWebRequest();

        if (req.result != UnityWebRequest.Result.Success)
        {
            Debug.LogWarning($"config failed {req.responseCode}: {req.error}");
            yield break;
        }
        onDone?.Invoke(JsonUtility.FromJson<DeviceConfig>(req.downloadHandler.text));
    }

    // POST /api/telemetry/usage
    // Unknown headsets are registered automatically (unassigned) on the first valid call.
    // client_event_id lets the server drop duplicates if you resend after a lost response.
    public IEnumerator SendEvent(string eventType, int durationSeconds, float completionPct, string simulationId = null)
    {
        var json = "{\\"headset_serial\\":\\"" + Serial + "\\",\\"data\\":{"
                 + "\\"event_type\\":\\"" + eventType + "\\","
                 + "\\"client_event_id\\":\\"" + Guid.NewGuid() + "\\","
                 + "\\"model\\":\\"" + SystemInfo.deviceModel + "\\","
                 + "\\"duration_seconds\\":" + durationSeconds + ","
                 + "\\"completion_pct\\":" + completionPct.ToString(System.Globalization.CultureInfo.InvariantCulture)
                 + (simulationId != null ? ",\\"simulation_id\\":\\"" + simulationId + "\\"" : "")
                 + "}}";

        using var req = new UnityWebRequest($"{BaseUrl}/api/telemetry/usage", "POST");
        req.uploadHandler = new UploadHandlerRaw(Encoding.UTF8.GetBytes(json));
        req.downloadHandler = new DownloadHandlerBuffer();
        req.SetRequestHeader("Content-Type", "application/json");
        req.SetRequestHeader("X-Device-Key", DeviceKey);
        yield return req.SendWebRequest();

        if (req.result != UnityWebRequest.Result.Success)
            Debug.LogWarning($"telemetry failed {req.responseCode}: {req.error}");
    }
}
`;
}

export default function DeviceApi() {
  const [savedKey, setSavedKey] = useState("");
  const [key, setKey] = useState("");
  const [source, setSource] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const confirmDialog = useConfirm();

  const load = async () => {
    try {
      const { data } = await api.get("/admin/device-key");
      setSavedKey(data.key);
      setKey(data.key);
      setSource(data.source);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const dirty = key !== savedKey;
  const baseUrl = serverUrl();
  const sample = useMemo(() => unitySample(baseUrl, savedKey), [baseUrl, savedKey]);

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { toast.error("Copy failed"); }
  };

  const save = async () => {
    const ok = await confirmDialog({
      title: "Change device key?",
      description: "Every headset still using the old key will be rejected until it is updated.",
      confirmText: "Change key",
      destructive: true,
    });
    if (!ok) return;
    setSaving(true);
    try {
      const { data } = await api.put("/admin/device-key", { key });
      setSavedKey(data.key); setKey(data.key); setSource(data.source);
      toast.success("Device key updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const generate = async () => {
    const ok = await confirmDialog({
      title: "Generate a new random key?",
      description: "The current key stops working immediately. Headsets must be updated with the new one.",
      confirmText: "Generate",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await api.post("/admin/device-key/generate");
      setSavedKey(data.key); setKey(data.key); setSource(data.source);
      setShow(true);
      toast.success("New device key generated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Integration"
        title="Device API"
        description="Headsets authenticate with this key. Unknown headsets that present a valid key are added to the Headsets list automatically, unassigned."
      />

      <div className="panel rounded-2xl p-5 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-purple-700" strokeWidth={1.75} />
          <Label className="text-xs uppercase tracking-[0.15em] text-slate-600">Device key</Label>
          {source ? (
            <Badge variant="outline" className="text-[10px] uppercase tracking-[0.12em] ml-2">
              {source === "database" ? "set in console" : source === "environment" ? "from server .env" : "not set"}
            </Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <Input
            data-testid="device-key-input"
            type={show ? "text" : "password"}
            autoComplete="off"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            className="font-mono max-w-md"
            placeholder="Enter a device key (8+ characters)"
          />
          <Button variant="ghost" size="sm" onClick={() => setShow(!show)} data-testid="device-key-toggle">
            {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={() => copy(savedKey, "Key")} disabled={!savedKey} data-testid="device-key-copy">
            <Copy className="h-4 w-4" />
          </Button>
          <Button onClick={save} disabled={!dirty || saving || key.trim().length < 8} className="btn-brand rounded-full" data-testid="device-key-save">
            <Save className="h-4 w-4 mr-2" /> Save
          </Button>
          <Button variant="outline" onClick={generate} className="rounded-full" data-testid="device-key-generate">
            <RefreshCw className="h-4 w-4 mr-2" /> Generate random
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Sent by devices in the <span className="font-mono">X-Device-Key</span> header. Changing it takes effect immediately.
        </p>
      </div>

      <div className="panel rounded-2xl p-5 mb-6">
        <div className="text-xs uppercase tracking-[0.15em] text-slate-600 mb-3">Endpoints</div>
        <div className="space-y-2 text-sm">
          <div><Badge variant="outline" className="font-mono text-[10px] mr-2">POST</Badge><span className="font-mono">{baseUrl}/api/telemetry/usage</span>
            <span className="text-slate-500"> — send events. Body: {"{ \"headset_serial\": \"...\", \"data\": { ... } }"}. Registers unknown headsets.</span></div>
          <div><Badge variant="outline" className="font-mono text-[10px] mr-2">GET</Badge><span className="font-mono">{baseUrl}/api/device/config?serial=&lt;headset id&gt;</span>
            <span className="text-slate-500"> — headset, patient and assigned modules. 401 bad key, 404 unknown serial.</span></div>
        </div>
      </div>

      <div className="panel rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-purple-100 flex items-center justify-between">
          <div className="text-xs uppercase tracking-[0.15em] text-purple-700">Unity (C#) example</div>
          <Button variant="ghost" size="sm" onClick={() => copy(sample, "Example")} data-testid="unity-sample-copy">
            <Copy className="h-4 w-4 mr-2" /> Copy
          </Button>
        </div>
        <pre className="p-4 text-xs leading-relaxed overflow-x-auto max-h-[60vh] bg-slate-950 text-slate-100" data-testid="unity-sample">
          <code>{sample}</code>
        </pre>
      </div>
    </div>
  );
}
